import * as Actions from '../actions/caseTypeStageActions';
import CaseTypeStage from '../../entities/caseTypeStage';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type CaseTypeStageAction = { 
    type: string; 
    caseTypeStage: CaseTypeStage | null; 
    caseTypeStageId: string | CaseTypeStage; 
    partial: boolean; 
    lastFetch: number; 
    caseTypeStages: CaseTypeStage[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<CaseTypeStage> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {CaseTypeStage[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<CaseTypeStage> | null, firmId: string, userId: string, updates?: CaseTypeStage[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'caseTypeStage', 'caseTypeStages', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<CaseTypeStage>} data
 * @param {CaseTypeStage[]} [updates]
 * @returns {ArrayMap<CaseTypeStage>}
 */
function updateStorage(data: ArrayMap<CaseTypeStage>, updates?: CaseTypeStage[]): ArrayMap<CaseTypeStage> {
	if (logging) console.debug('⮂ caseTypeStageReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {CaseTypeStage[]} [updates]
 * @returns {ArrayMap<CaseTypeStage>}
 */
function updateStorageNoReturn(updates?: CaseTypeStage[]): void {
	if (logging) console.debug('⮂ caseTypeStageReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {CaseTypeStage[]} [data]
 * @returns {ArrayMap<CaseTypeStage>}
 */
function createState(data?: CaseTypeStage[] | ArrayMap<CaseTypeStage>): ArrayMap<CaseTypeStage> {
	if (!data) return ArrayMapRegistry.clone<CaseTypeStage>('caseTypeStage', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<CaseTypeStage>('caseTypeStage', data.map(e => e));
    else return ArrayMapRegistry.clone<CaseTypeStage>('caseTypeStage', data);
}

function caseTypeStages(state: ArrayMap<CaseTypeStage>, action: CaseTypeStageAction): ArrayMap<CaseTypeStage> {
	if (!state) state = createState();

	let st: ArrayMap<CaseTypeStage>;

	switch (action.type) {
		// action.caseTypeStage - the new value of caseTypeStage with id action.id
		// action.caseTypeStageId - id of the target, used if new value is null
		case Actions.CASETYPESTAGE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ caseTypeStageReducer: FETCH_ONE_SUCCESS', action);

			if (action.caseTypeStage === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.caseTypeStageId)) {
				st = state.clone();
				st.set(action.caseTypeStage);
				// state.clear();

				updateStorageNoReturn([action.caseTypeStage]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.caseTypeStages
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.caseTypeStages
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.caseTypeStages
			 !partial, !stateEmpty, fetchEmpty	-> D return caseTypeStages in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge caseTypeStages in state newer than those in action.caseTypeStages
		 */
		case Actions.CASETYPESTAGE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.caseTypeStages.length === 0;
			const fetched = action.caseTypeStages.map(caseTypeStage => new CaseTypeStage({...caseTypeStage, persisted: true}));

			if (logging) console.debug(`⮂ caseTypeStageReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} caseTypeStages with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ caseTypeStageReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ caseTypeStageReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<CaseTypeStage> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: CaseTypeStage[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(caseTypeStage => {
					const current = st.get(caseTypeStage.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && caseTypeStage.status === 'D') {
							// Remove it from state
							toUpdate.push(caseTypeStage);
							st.delete(caseTypeStage);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (caseTypeStage.addedOn > current.addedOn) {
								toUpdate.push(caseTypeStage);
								st.set(caseTypeStage);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (caseTypeStage.status !== 'D') {
							toUpdate.push(caseTypeStage);
							st.set(caseTypeStage);
						}
						else if (logging) console.debug('⮂ caseTypeStageReducer: partialMerge: skipping deleted caseTypeStage not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ caseTypeStageReducer: empty: caseTypeStages', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ caseTypeStageReducer: fullMerge: caseTypeStages', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localCaseTypeStage => {
                    // If this caseTypeStage has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localCaseTypeStage.persisted) {
                        const fetchedCaseTypeStage = st.get(localCaseTypeStage.id);
                        if (!fetchedCaseTypeStage || localCaseTypeStage.addedOn > fetchedCaseTypeStage.addedOn) st.set(localCaseTypeStage);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.CASETYPESTAGE_FETCH_ONE_FAILURE:
			console.error('CASETYPESTAGE_FETCH_ONE_FAILURE', action.caseTypeStageId, action.err);

			// No update to storage required
			return state;

		case Actions.CASETYPESTAGE_FETCH_FAILURE:
			console.error('CASETYPESTAGE_FETCH_FAILURE', action.caseTypeStageId, action.err);

			// No update to storage required
			return state;

		case Actions.CASETYPESTAGE_RESTORE: {
		    let caseTypeStage = new CaseTypeStage({...action.caseTypeStage});
            st = state.clone();

            const existing = st.get(caseTypeStage.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (caseTypeStage.addedOn >= existing.addedOn) {
                    st.set(caseTypeStage);
                    return updateStorage(st, [caseTypeStage]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (caseTypeStage.status === 'D') return state;

                st.set(caseTypeStage);
                return updateStorage(st, [caseTypeStage]);
            }
        }
            
		case Actions.CASETYPESTAGE_SAVE: {
		    // console.debug('⮂ CASETYPESTAGE_SAVE reducer', action.caseTypeStage?.id);
		    
			// clone the caseTypeStage
			let caseTypeStage = new CaseTypeStage({...action.caseTypeStage});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new caseTypeStage in the state
			// If this is a create, add to the queue
			const _caseTypeStage = st.get(caseTypeStage.id);

			if (!_caseTypeStage) st.set(caseTypeStage);
			else {
				if (caseTypeStage.addedOn >= _caseTypeStage.addedOn) st.set(caseTypeStage);
			}

			return updateStorage(st, [caseTypeStage]);
		}

		case Actions.CASETYPESTAGE_SAVE_MANY: {
		    if (logging) console.debug('⮂ CASETYPESTAGE_SAVE_MANY reducer', action.caseTypeStages.map(caseTypeStage => caseTypeStage.id).join(','));
		    
			// clone the caseTypeStages
			let toProcess = action.caseTypeStages.map(caseTypeStage => new CaseTypeStage(caseTypeStage));

			st = state.clone();
			// state.clear();

			const updates: CaseTypeStage[] = [];

			toProcess.forEach(caseTypeStage => {
				// Add or update the new caseTypeStage in the state
				// If this is a create, add to the queue
				const existing = st.get(caseTypeStage.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (caseTypeStage.status !== 'D') {
						st.push(caseTypeStage);
						updates.push(caseTypeStage);
					}
					else if (logging) console.debug('⮂ CASETYPESTAGE_SAVE_MANY: skipping deleted caseTypeStage not in state');
				}
				else {
					if (caseTypeStage.addedOn >= existing.addedOn) {
						st.set(caseTypeStage);
						updates.push(caseTypeStage);
					}
					else if (logging) console.debug('⮂ Skipping, existing caseTypeStage had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CASETYPESTAGE_REMOVE: {
			// clone the caseTypeStage
			const caseTypeStage = new CaseTypeStage({...action.caseTypeStage});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(caseTypeStage);

			return updateStorage(st, [caseTypeStage]);
		}

		case Actions.CASETYPESTAGE_RESTORE_FAILURE:
		case Actions.CASETYPESTAGE_SAVE_FAILURE: {
		    console.warn('CASETYPESTAGE_SAVE_FAILURE reducer', action.caseTypeStage?.id);
		    if (!action.caseTypeStage?.id) {
				console.warn('CASETYPESTAGE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const caseTypeStage = ensure(CaseTypeStage, action.caseTypeStage);
			if (!caseTypeStage) {
				console.error('Could not find caseTypeStage after call to save');
				return state;
			}

			st = state.clone();

			const _caseTypeStageRef = state.get(caseTypeStage.id);

			if (_caseTypeStageRef && (_caseTypeStageRef.addedOn === caseTypeStage.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _caseTypeStage = new CaseTypeStage({..._caseTypeStageRef});
				_caseTypeStage.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_caseTypeStage.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('caseTypeStage', _caseTypeStage).then(r => {
						if (r.isErr()) console.error(`⮂ caseTypeStageReducer: failed to save caseTypeStage with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ caseTypeStageReducer: saved caseTypeStage with fatalError info');
					}).catch(err => console.error(`⮂ caseTypeStageReducer: failed to save caseTypeStage with fatalError info: ${err.message}`));

				st.set(_caseTypeStage);
			}

			return updateStorage(st);
		}

		case Actions.CASETYPESTAGE_SAVE_MANY_FAILURE: {
		    console.warn('CASETYPESTAGE_SAVE_MANY_FAILURE reducer', action.caseTypeStages.map(caseTypeStage => caseTypeStage.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.CASETYPESTAGE_RESTORE_SUCCESS:
		case Actions.CASETYPESTAGE_SAVE_SUCCESS: {
			if (!action.caseTypeStage?.id) {
				console.warn('CASETYPESTAGE_SAVE_SUCCESS reducer');
                return state;
			}

			const caseTypeStageRef = state.get(action.caseTypeStage?.id);
			if (!caseTypeStageRef) {
				console.warn('Could not find caseTypeStage after call to save');
                return state;
			}

			st = state.clone();

			if (action.caseTypeStage.addedOn >= caseTypeStageRef.addedOn) {
				st.set(action.caseTypeStage);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const caseTypeStage = new CaseTypeStage({...caseTypeStageRef, persisted: true});
				st.set(caseTypeStage);
			}

			return updateStorage(st, [action.caseTypeStage]);
		}

		case Actions.CASETYPESTAGE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ CASETYPESTAGE_SAVE_MANY_SUCCESS reducer', action.caseTypeStages.map(caseTypeStage => caseTypeStage.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const caseTypeStages: CaseTypeStage[] = [];

			action.caseTypeStages.forEach(e => {
				const caseTypeStageRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!caseTypeStageRef) console.warn('Could not find caseTypeStage after call to save');
				else if (e.addedOn >= caseTypeStageRef.addedOn) {
				    st.set(e);
				    caseTypeStages.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const caseTypeStage = new CaseTypeStage({...caseTypeStageRef, persisted: true});
					st.set(caseTypeStage);
					caseTypeStages.push(caseTypeStage);
				}
			});

			return updateStorage(st, caseTypeStages);
		}

		case Actions.CASETYPESTAGE_REMOVE_FAILURE: {
		    console.warn('CASETYPESTAGE_REMOVE_FAILURE action', action.caseTypeStage?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.CASETYPESTAGE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.CASETYPESTAGE_RESTORE_MANY: {
			if (logging) console.debug('⮂ CASETYPESTAGE_RESTORE_MANY reducer', action.caseTypeStages.map(caseTypeStage => caseTypeStage.id).join(','));

			const toProcess = action.caseTypeStages.map(caseTypeStage => new CaseTypeStage({...caseTypeStage}));
			st = state.clone();

			const updates: CaseTypeStage[] = [];

			toProcess.forEach(caseTypeStage => {
				const existing = st.get(caseTypeStage.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (caseTypeStage.addedOn >= existing.addedOn) {
						st.set(caseTypeStage);
						updates.push(caseTypeStage);
					}
					else if (logging) console.debug('⮂ CASETYPESTAGE_RESTORE_MANY: skipping older version of caseTypeStage');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (caseTypeStage.status !== 'D') {
						st.set(caseTypeStage);
						updates.push(caseTypeStage);
					}
					else if (logging) console.debug('⮂ CASETYPESTAGE_RESTORE_MANY: skipping deleted caseTypeStage not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CASETYPESTAGE_REMOVE_MANY: {
			if (logging) console.debug('⮂ CASETYPESTAGE_REMOVE_MANY reducer', action.caseTypeStages.map(caseTypeStage => caseTypeStage.id).join(','));

			const toProcess = action.caseTypeStages.map(caseTypeStage => new CaseTypeStage({...caseTypeStage}));
			st = state.clone();

			toProcess.forEach(caseTypeStage => {
				st.delete(caseTypeStage);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default caseTypeStages;